import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import "./whycho.css";

export default function WhyChooseAnimated({
  imageSrc = "/e77.png",
  overlayText = "Aerlis Gro",
  typeSpeed = 80,
  deleteSpeed = 45,
  holdTime = 1400,
}) {
  /* ================= TYPEWRITER ================= */
  const [display, setDisplay] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [index, setIndex] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;

    const reduce =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduce) {
      setDisplay(overlayText);
      return () => (mounted.current = false);
    }

    let timer;

    const tick = () => {
      const full = overlayText;

      if (!isDeleting) {
        const next = full.slice(0, index + 1);
        setDisplay(next);
        setIndex((i) => i + 1);

        if (next === full) {
          timer = setTimeout(() => {
            if (!mounted.current) return;
            setIsDeleting(true);
            setIndex((i) => i - 1);
          }, holdTime);
        } else {
          timer = setTimeout(tick, typeSpeed);
        }
      } else {
        const next = full.slice(0, Math.max(0, index));
        setDisplay(next);
        setIndex((i) => Math.max(0, i - 1));

        if (next === "") {
          setIsDeleting(false);
          setIndex(0);
          timer = setTimeout(tick, 200);
        } else {
          timer = setTimeout(tick, deleteSpeed);
        }
      }
    };

    timer = setTimeout(tick, typeSpeed);
    return () => {
      mounted.current = false;
      clearTimeout(timer);
    };
  }, [overlayText, typeSpeed, deleteSpeed, holdTime, isDeleting, index]);

  /* ================= ANIMATIONS ================= */
  const container = {
    hidden: { opacity: 0, y: 12 },
    show: { opacity: 1, y: 0, transition: { staggerChildren: 0.08 } },
  };

  const item = {
    hidden: { opacity: 0, y: 8 },
    show: { opacity: 1, y: 0, transition: { duration: 0.35 } },
  };

  const hoverFloat = {
    rest: { y: 0, scale: 1 },
    hover: { y: -6, scale: 1.05, transition: { duration: 0.25 } },
  };

  /* ================= ICON CARDS ================= */
  const cards = [
    { title: "EXPERIENCED", icon: "/icons/EXPERIENCEDS.png" },
    { title: "TRUSTED", icon: "/icons/TRUSTEDS.png" },
    { title: "PROFESSIONAL", icon: "/icons/PROFESSIONALS.png" },
    { title: "24×7 SUPPORT", icon: "/icons/24.png" },
  ];

  return (
    <section className="why-section">
      <div className="why-container">
        <motion.div initial="hidden" animate="show" variants={container}>
          <motion.h2 variants={item} className="why-title">
            Why Choose Aerlis Gro
          </motion.h2>

          <motion.div variants={item} className="why-maincard">
            {/* TOP CONTENT */}
            <div className="why-top">
              <div className="why-left">
                <p className="why-intro">
                  At Aerlis Gro, your financial success is our commitment.
                </p>

                <p className="why-list">
  Aerlis Gro is a trusted insurance partner focused on transparency, honest guidance, and a true customer-first approach. 
  We simplify the complexities of insurance and provide solutions tailored to your unique needs, ensuring clarity and confidence in every decision you make. 
  Our support extends through every step of your journey — from selecting the right policy to assisting you with smooth, stress-free claim processes. 
  Backed by a reliable and responsive team, we make insurance easy, accessible, and reassuring, no matter your situation. 
  At Aerlis Gro, we believe in long-term relationships built on trust, consistency, and genuine care for your wellbeing. 
  With us, you receive more than just a policy — you gain a committed partner dedicated to protecting your future, securing your finances, and giving you complete peace of mind.
</p>


                <p className="why-bottom">
                  We don’t just sell policies — we build lasting relationships.
                </p>
              </div>

              <motion.div
                className="why-illustration"
                whileHover={{ y: -6 }}
              >
                <img src={imageSrc} alt="Aerlis Gro" />
                <div className="overlay-text">
                  <span>{display}</span>
                  <span className="typed-caret">▌</span>
                </div>
              </motion.div>
            </div>

            {/* GRID INSIDE CARD */}
            <div className="why-grid">
              {cards.map((c) => (
                <motion.div
                  key={c.title}
                  className="why-card"
                  variants={hoverFloat}
                  initial="rest"
                  whileHover="hover"
                >
                  <div className="icon-circle">
                    <img src={c.icon} alt={c.title} />
                  </div>
                  <h3 className="card-title">{c.title}</h3>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
